import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import type * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

/**
 * Subscribes to entity change events (create/update) from the API's onChange callback
 * and updates the cache when entities are added or updated.
 *
 * When the context changes, it unsubscribes from the previous observable,
 * calls the callback with the new context, and subscribes to the new observable.
 */
export const subscribeChange = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) => {
    const onChange = collection.api.onChange
    if (!onChange) {
      // No subscription support for entity changes
      return undefined
    }

    return collection.feature.selectors.context.asObservable.pipe(
      // TODO: Note that switchMap will unsubscribe from the previous subscription
      // TODO: Enhance onChange callback signature to include queryRequest and
      // currentEntities to enable intelligent insertion/sorting
      rxjs.switchMap(context =>
        onChange(context).pipe(
          rxjs.switchMap(entity =>
            rxjs.from([
              collection.plugins.cachePlugin.feature.actions.put
                .from('api.onChange.subscription')
                .create({ entity }),
              collection.plugins.currentListPlugin.feature.actions.insert
                .from('api.onChange.subscription')
                .create({ ids: [collection.identify(entity)] }),
            ]),
          ),
        ),
      ),
    )
  },
)
